import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { EmptyState, Skeleton } from "../../components/ui/Feedback";
import { Input, Select } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { majorToMinor, minorToMajor } from "../../lib/money";
import type {
	DiscountRule,
	DiscountRulePatch,
	GiftDiscountKind,
	PurchaseType,
	SelectorKind,
} from "../../lib/types";
import { SellingPlanOptions } from "../products/SellingPlanOptions";
import { usePlansForProducts } from "../products/usePlansForProducts";
import { SelectorField } from "./SelectorField";
import {
	useCreateDiscount,
	useDeleteDiscount,
	useDiscountRules,
	useUpdateDiscount,
} from "./useDiscountRules";

const KINDS: SelectorKind[] = ["product", "variant", "product_type", "vendor", "tag", "any"];

const PURCHASE_HELP =
	"Whether the markdown applies to a one time purchase, a subscription, or both. Use subscription only to reward shoppers for choosing a plan.";

const TAG_HELP =
	"Tags are resolved to the products carrying them when you publish. A tag you add to a product later is picked up on the next publish, not automatically.";

const NEW_DISCOUNT: DiscountRulePatch = {
	selectorKind: "product_type",
	selectorValues: [],
	discountKind: "percentage",
	discountValue: 2000,
	purchaseType: "any",
	sellingPlanGid: null,
};

const DiscountCard = ({
	rule,
	campaignId,
	index,
}: {
	rule: DiscountRule;
	campaignId: string;
	index: number;
}) => {
	const update = useUpdateDiscount(campaignId);
	const remove = useDeleteDiscount(campaignId);
	const toast = useToast();
	const confirm = useConfirm();

	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };
	const save = (patch: DiscountRulePatch) => update.mutate({ ruleId: rule.id, ...patch }, notify);

	// A plan can only be pinned when the selection is products we can look plans
	// up for, and only when those products actually have any.
	const targetsProducts = rule.selectorKind === "product" || rule.selectorKind === "variant";
	const plans = usePlansForProducts(targetsProducts ? rule.selectorValues : []);
	const available = Object.values(plans.data?.plans ?? {}).flat();
	const byGid = new Map(available.map((plan) => [plan.gid, plan]));
	const canSubscribe = !targetsProducts || available.length > 0;

	return (
		<div className="space-y-4 rounded-md border border-line bg-surface p-4">
			<div className="flex items-center justify-between gap-3">
				<span className="text-xs font-medium text-ink">Discount {index + 1}</span>
				<Button
					variant="ghost"
					size="sm"
					aria-label="Delete discount"
					onClick={() =>
						confirm({
							title: "Delete this discount?",
							description: "It stops applying after the next publish.",
							onConfirm: () =>
								remove.mutate(rule.id, {
									onSuccess: () => toast.push("Discount deleted"),
									...notify,
								}),
						})
					}
				>
					<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
				</Button>
			</div>

			<div className="grid gap-4 sm:grid-cols-3">
				<label className="block space-y-1.5">
					<span className="text-sm text-ink">Take off</span>
					<Select
						value={rule.discountKind}
						onChange={(event) =>
							save({ discountKind: event.target.value as GiftDiscountKind, discountValue: 0 })
						}
					>
						<option value="percentage">Percentage</option>
						<option value="fixed_amount">Amount</option>
						<option value="free">Make it free</option>
					</Select>
				</label>

				{rule.discountKind === "free" ? null : (
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">
							{rule.discountKind === "percentage" ? "Percent off" : "Amount off"}
						</span>
						<Input
							type="number"
							min="0"
							max={rule.discountKind === "percentage" ? "100" : undefined}
							step={rule.discountKind === "percentage" ? "1" : "0.01"}
							defaultValue={
								rule.discountKind === "percentage"
									? String(rule.discountValue / 100)
									: minorToMajor(rule.discountValue)
							}
							onBlur={(event) =>
								save({
									discountValue:
										rule.discountKind === "percentage"
											? Math.round(Number(event.target.value || "0") * 100)
											: majorToMinor(event.target.value || "0"),
								})
							}
						/>
					</label>
				)}
			</div>

			<div className="border-t border-line pt-4">
				<SelectorField
					label="Applies to"
					kind={rule.selectorKind}
					values={rule.selectorValues}
					kinds={KINDS}
					onChange={(next) => save({ selectorKind: next.kind, selectorValues: next.values })}
				/>
				{rule.selectorKind === "tag" ? (
					<p className="mt-2 text-xs text-ink-muted">{TAG_HELP}</p>
				) : null}
			</div>

			<div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-2">
				<label className="block space-y-1.5">
					<LabelWithHint label="Purchase type">{PURCHASE_HELP}</LabelWithHint>
					<Select
						value={rule.purchaseType}
						onChange={(event) =>
							save({ purchaseType: event.target.value as PurchaseType, sellingPlanGid: null })
						}
					>
						<option value="any">One time or subscription</option>
						<option value="one_time">One time only</option>
						{canSubscribe ? <option value="subscription">Subscription only</option> : null}
					</Select>
					{canSubscribe ? null : (
						<span className="block text-xs text-ink-muted">
							None of the selected products are sold on a subscription.
						</span>
					)}
				</label>

				{rule.purchaseType === "subscription" && targetsProducts && available.length > 0 ? (
					<label className="block space-y-1.5">
						<span className="text-sm text-ink">Plan</span>
						<Select
							value={rule.sellingPlanGid ?? ""}
							onChange={(event) => save({ sellingPlanGid: event.target.value || null })}
						>
							<option value="">Any plan</option>
							<SellingPlanOptions plans={[...byGid.values()]} />
						</Select>
					</label>
				) : null}
			</div>
		</div>
	);
};

export const DiscountEditor = ({ campaignId }: { campaignId: string }) => {
	const rules = useDiscountRules(campaignId);
	const create = useCreateDiscount(campaignId);
	const toast = useToast();

	const items = rules.data?.items ?? [];

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="max-w-md text-xs text-ink-muted">
					Each one marks down whatever it applies to, with no minimum spend and nothing for the
					shopper to do. Several discounts stack across different products.
				</p>
				<Button
					variant="outline"
					size="sm"
					disabled={create.isPending}
					onClick={() =>
						create.mutate(NEW_DISCOUNT, {
							onSuccess: () => toast.push("Discount added"),
							onError: (error) => toast.push(error.message, "error"),
						})
					}
				>
					<Plus className="h-3.5 w-3.5" strokeWidth={2} />
					Add discount
				</Button>
			</div>

			{rules.isPending ? <Skeleton className="h-40 w-full" /> : null}

			{rules.data && items.length === 0 ? (
				<EmptyState
					title="No discounts yet"
					description="Add one to mark down a product, a product type, a vendor or a tag."
				/>
			) : null}

			{items.map((rule, index) => (
				<DiscountCard key={rule.id} rule={rule} campaignId={campaignId} index={index} />
			))}
		</div>
	);
};
